import { useCallback, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import {
  clearAiWidgetMaps,
  selectActiveAiWidgetMap,
  selectAiWidgetMapCount,
  selectAiWidgetMapIndex,
  setAiWidgetMapIndex,
} from "@/reducers/aiWidgetMapSlice"
import LightMapView, {
  LightMapViewHandle,
} from "@/components/maps/LightMapView"
import AgentMapLayers from "@/components/slickAI/AgentMapLayers"
import AgentMapControls from "@/components/slickAI/AgentMapControls"
import AgentMapFullscreenDialog from "@/components/slickAI/AgentMapFullscreenDialog"
import { computeBounds } from "@/lib/aiWidgetMapHelpers"
import type { MapStyle } from "@/components/maps/MapManager"

type AgentMapPanelProps = {
  chatJid: string
  // Hide internal navigation pagination and close-widget control. Used when
  // the surrounding wrapper (e.g. MapWidget) renders its own header chrome.
  hideChrome?: boolean
}

type AgentMapNavProps = {
  chatJid: string
  count: number
  index: number
}

export const AgentMapNav = ({ chatJid, count, index }: AgentMapNavProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const onPrev = useCallback(() => {
    dispatch(setAiWidgetMapIndex({ chatJid, index: index - 1 }))
  }, [dispatch, chatJid, index])
  const onNext = useCallback(() => {
    dispatch(setAiWidgetMapIndex({ chatJid, index: index + 1 }))
  }, [dispatch, chatJid, index])

  return (
    <div className="flex items-center justify-center gap-3 border-t border-border bg-background px-2 py-1">
      <button
        type="button"
        onClick={onPrev}
        disabled={index === 0}
        className="rounded px-2 py-0.5 text-sm hover:bg-accent disabled:opacity-40"
        aria-label={t("ai.map.previous")}
      >
        ←
      </button>
      <span className="text-xs text-muted-foreground">
        {index + 1} / {count}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={index === count - 1}
        className="rounded px-2 py-0.5 text-sm hover:bg-accent disabled:opacity-40"
        aria-label={t("ai.map.next")}
      >
        →
      </button>
    </div>
  )
}

const AgentMapPanel = ({ chatJid, hideChrome }: AgentMapPanelProps) => {
  const mapData = useAppSelector(selectActiveAiWidgetMap(chatJid))
  const count = useAppSelector(selectAiWidgetMapCount(chatJid))
  const index = useAppSelector(selectAiWidgetMapIndex(chatJid))

  const [mapStyle, setMapStyle] = useState<MapStyle>("default")
  const [fullscreenOpen, setFullscreenOpen] = useState(false)
  const mapHandleRef = useRef<LightMapViewHandle>(null)

  const bounds = useMemo(
    () => (mapData ? computeBounds(mapData) : null),
    [mapData],
  )

  const fitAll = useCallback(() => {
    if (bounds && mapHandleRef.current) {
      mapHandleRef.current.fitBounds(bounds)
    }
  }, [bounds])

  const dispatch = useAppDispatch()
  const handleClose = useCallback(() => {
    dispatch(clearAiWidgetMaps({ chatJid }))
  }, [dispatch, chatJid])

  if (!mapData || count === 0) return null

  return (
    <div className="flex h-full w-full flex-col">
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <LightMapView
          ref={mapHandleRef}
          fitBoundsTo={bounds}
          mapStyle={mapStyle}
          className="absolute inset-0"
        >
          <AgentMapLayers mapData={mapData} generation={index} />
        </LightMapView>
        <AgentMapControls
          currentStyle={mapStyle}
          onChangeStyle={setMapStyle}
          onFitAll={fitAll}
          canFitAll={!!bounds}
          onToggleFullscreen={() => setFullscreenOpen(true)}
          isFullscreen={false}
          onCloseWidget={hideChrome ? undefined : handleClose}
        />
      </div>
      {!hideChrome && count > 1 && (
        <AgentMapNav chatJid={chatJid} count={count} index={index} />
      )}
      <AgentMapFullscreenDialog
        open={fullscreenOpen}
        onOpenChange={setFullscreenOpen}
        chatJid={chatJid}
        mapData={mapData}
        count={count}
        index={index}
        bounds={bounds}
        mapStyle={mapStyle}
        onChangeMapStyle={setMapStyle}
      />
    </div>
  )
}

export default AgentMapPanel
